import { api } from '@/api';
import { env } from '@/lib';
import type {
  Language,
  TranslationsResponse,
  TranslationQueryParams,
  UpdateTranslationRequest,
  ImportResult,
} from '../types';

export async function fetchLanguages(): Promise<Language[]> {
  return api.get<Language[]>('/v1/languages');
}

export async function fetchTranslations(
  params: TranslationQueryParams
): Promise<TranslationsResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    language_id: params.language_id,
    page: params.page,
    page_size: params.page_size,
    search: params.search || undefined,
    new_only: params.new_only || undefined,
  };

  return api.get<TranslationsResponse>('/v1/translations', { params: queryParams });
}

export async function updateTranslation(
  translationId: string,
  languageId: number,
  data: UpdateTranslationRequest
): Promise<void> {
  return api.put(`/v1/translations/${translationId}/language/${languageId}`, data);
}

export async function exportTranslations(languageId: number): Promise<void> {
  const url = `${env.apiBaseUrl}/v1/translations/export/${languageId}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `strings_${languageId}.xml`;
  
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }
  }

  const downloadUrl = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(downloadUrl);
  }
}

/** Marks the given translation IDs as no longer new for the specified language. */
export async function clearNewEntries(
  languageId: number,
  translationIds: string[]
): Promise<void> {
  await api.post(`/v1/translations/language/${languageId}/clear-new`, { translation_ids: translationIds });
}

export async function importTranslations(
  languageId: number,
  file: File
): Promise<ImportResult> {
  const url = `${env.apiBaseUrl}/v1/translations/import/${languageId}`;
  
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Import failed');
  }

  return response.json();
}
